import { Content } from "@robe/core";
import { useMemo } from "react";
import { LayoutProducts, useLayout } from "xcore";
import { Office, ProductOverviewPageValues } from "xcore/types";
import { NavItemProps } from "./NavItem";

export const useHeaderMenu = (productsData: LayoutProducts) => {
  const {
    header: { nav },
    general,
    stringify,
    core,
    localize,
  } = useLayout();

  const homepage = useMemo<NavItemProps>(
    () => ({
      content: nav.homepage,
      activeOn: ["homepage"],
      children: stringify(nav.homepage?.title),
    }),
    [nav.homepage, stringify]
  );

  const spotOnField = localize(nav.spotOn.values?.itemsRelation) ?? [];

  const spotOn = {
    activeOn: ["spotOn", "generalSpotOn", "videoSpotOn"],
    dropdownItems: [...spotOnField.map((r) => [{ children: stringify(r.title), content: r }, []])] as unknown as [
      NavItemProps,
      NavItemProps[],
    ][],
    children: stringify(nav.spotOn.title),
  };

  const products = useMemo<NavItemProps>(() => {
    const productItems = localize(productsData?.values?.itemsRelation) ?? [];
    // eslint-disable-next-line max-len
    const productItemsFiltered = productItems?.filter((p) => p?.type && p?.slug && p?.title && p?.values) as Pick<
      Content,
      "id" | "type" | "title" | "values" | "slug"
    >[];

    return {
      content: productsData as Content,
      activeOn: ["products", "product", "productTypePage", "applicationPage", "beamTypePage", "productOverviewPage", "productFamilyPage"],
      overviewLink: {
        content: productsData as Content,
        children: stringify(general.values?.productOverviewButton),
      },
      children: stringify(productsData?.title),
      dropdownItems: productItemsFiltered?.map((p) => [
        { children: stringify(p.title), content: p },
        localize((p?.values as ProductOverviewPageValues)?.itemsRelation)
          ?.filter((k) => k?.type && k?.slug && k?.title && k?.values)
          .map((x) => ({ children: stringify(x.title), content: x as Content })) ?? [],
      ]),
    };
  }, [localize, productsData, stringify, general.values?.productOverviewButton]);

  const innovations = useMemo<NavItemProps>(
    () => ({
      content: nav.innovations,
      activeOn: ["innovation", "innovations"],
      children: stringify(nav.innovations.title),
    }),
    [nav.innovations, stringify]
  );

  const support = useMemo<NavItemProps>(
    () => ({
      content: nav.support,
      activeOn: ["support"],
      children: stringify(nav.support.title),
    }),
    [nav.support, stringify]
  );

  const news = useMemo<NavItemProps>(
    () => ({
      content: nav.newsPage,
      activeOn: ["news", "newsPage"],
      children: stringify(nav.newsPage.title),
    }),
    [nav.newsPage, stringify]
  );

  const sustainability = useMemo<NavItemProps>(
    () =>
      nav.sustainability && {
        content: nav.sustainability,
        activeOn: ["sustainability"],
        children: stringify(nav.sustainability.title),
      },
    [nav.sustainability, stringify]
  );

  const aboutUs = useMemo<NavItemProps<typeof nav.aboutUs>>(() => {
    const dropdownBase: [NavItemProps, NavItemProps[] | undefined][] = [
      [{ content: nav.aboutUs, anchor: "company", children: stringify(nav.aboutUs.values.title) }, []],
      [{ content: nav.aboutUs, anchor: "eu", children: stringify(nav.aboutUs.values.madeInEUHeading) }, []],
      [{ content: nav.aboutUs, anchor: "owners", children: stringify(nav.aboutUs.values.owners) }, []],
      [{ content: nav.aboutUs, anchor: "history", children: stringify(nav.aboutUs.values.history) }, []],
      [{ content: nav.aboutUs, anchor: "career", children: stringify(nav.aboutUs.values.career) }, []],
    ];

    return {
      content: nav.aboutUs,
      activeOn: ["aboutUs", "job", "patents", "trademarks"],
      dropdownItems: core.localeContent.includes("en")
        ? [
          ...dropdownBase,
          [{ link: stringify(nav.aboutUs.values.careerExtLink), children: stringify(nav.aboutUs.values.dropdownCareerCz) }, []],
          [{ content: nav.aboutUs, anchor: "legal", children: stringify(nav.aboutUs.values.legal) }, []],
        ]
        : [...dropdownBase, [{ content: nav.aboutUs, anchor: "legal", children: stringify(nav.aboutUs.values.legal) }, []]],
      children: stringify(nav.aboutUs.title),
    };
  }, [core.localeContent, nav.aboutUs, stringify]);

  const defaultOffice = (localize(nav.offices.values?.defaultContactPage) ?? nav.offices.values.officeList?.[0]?.offices?.[0]) as Office;

  const contacts = useMemo<NavItemProps>(
    () => ({
      content: defaultOffice,
      activeOn: ["office"],
      withLabels: true,
      dropdownItems: nav.offices.values.officeList
        ?.filter((o) => o.offices && o.offices.length > 0 && o?.offices.every((item) => item?.type && item?.slug))
        .map((c) => [
          { children: stringify(c.header), content: c.offices?.[0] as Office },
          c.offices?.map((o) => ({ children: stringify(o.title), content: o as Office })) ?? [],
        ]),
      children: stringify(nav.offices.title),
    }),
    [defaultOffice, nav.offices, stringify]
  );

  return {
    homepage,
    spotOn,
    products,
    innovations,
    support,
    news,
    sustainability,
    aboutUs,
    contacts,
  };
};
